import {
  Buildings,
  CalendarBlank,
  ChatCircleText,
  CheckSquareOffset,
  ClockCounterClockwise,
  DeviceMobile,
  EnvelopeSimple,
  GearSix,
  Globe,
  House,
  IdentificationCard,
  Bell,
  Lightbulb,
  Pulse as PulseNav,
  Receipt,
  ShieldWarning,
  Storefront,
  Tag,
  UserCircle,
  UserGear,
  Users,
  UsersThree,
  type Icon,
} from '@phosphor-icons/react';

// Sidebar nav model. Three tiers, Stripe-style:
//
//   1. PRIMARY_NAV  — always-visible top items. The most-trafficked pages.
//                     Never enter the "Recently visited" list (they have a
//                     permanent slot, recents would be redundant).
//   2. HUB_NAV      — top-level "main" pages rendered in the "All pages"
//                     accordion. A hub item like `settings` represents a
//                     surface with sub-pages — clicking it opens the
//                     settings panel rather than navigating to a route.
//                     Single-page hubs (e.g. `website`) navigate normally.
//   3. DEEP_NAV     — sub-pages (Settings → Log, Administrators, Danger
//                     zone). NEVER shown in "All pages" — only surface in
//                     "Recently visited" after the user has actually been
//                     there. From recents the user can pin them so they
//                     stick.
//
// Adding a new feature:
//   - Top-level page that earns a permanent sidebar slot → PRIMARY_NAV.
//   - Top-level page (its own URL or panel surface) → HUB_NAV.
//   - Sub-page reachable from a hub → DEEP_NAV.
// Always wire `i18nKey` in common.json across all 5 locales.

export interface NavItem {
  id: string;
  i18nKey: string;
  icon: Icon;
  // Path suffix appended to `/orgs/:slug`. Empty string targets the org
  // root. Omitted on action items (e.g. the Settings hub that opens a
  // panel rather than navigating).
  to?: string;
  // True when this is an "action" item — clicking it should trigger a
  // panel/menu rather than navigating to a route. Currently only used by
  // the Settings hub row, which opens the SettingsPanel slide-over.
  opensSettings?: boolean;
}

export const PRIMARY_NAV: readonly NavItem[] = [
  { id: 'home', to: '', i18nKey: 'nav.home', icon: House },
  { id: 'members', to: '/members', i18nKey: 'nav.members', icon: Users },
  { id: 'teams', to: '/teams', i18nKey: 'nav.teams', icon: UsersThree },
  // Team categories — the grouping (Senior / Youth / Veteran) that drives the
  // schedule-rule and notice-fee defaults teams inherit. Promoted to a permanent
  // top slot next to Teams: it's become a central setup surface a club has to
  // configure for everything else to work smoothly, not a buried config page.
  // Route stays /settings/team-categories; the URL-driven ?edit=<id>&tab=…
  // drawer keeps the AI navigate deep-link intact.
  { id: 'team-categories', to: '/settings/team-categories', i18nKey: 'nav.settingsTeamCategories', icon: Tag },
  { id: 'schedule', to: '/schedule', i18nKey: 'nav.schedule', icon: CalendarBlank },
  // Notices ("aviseringar") — the proactive season-renewal / fee-request
  // engine. Promoted to a permanent top slot: admins run seasonal notice
  // batches and track collection on a recurring cadence, so it earns a
  // fixed home rather than living in the "All pages" accordion.
  { id: 'notices', to: '/notices', i18nKey: 'nav.notices', icon: Bell },
];

export const HUB_NAV: readonly NavItem[] = [
  // Attendance — per-team grid for confirming who showed up. Lives next
  // to the primary nav because leaders hit it on a recurring cadence
  // (after every practice / match). The page itself drives team + date
  // range via query params, so the hub link just points at the surface.
  { id: 'attendance', to: '/attendance', i18nKey: 'nav.attendance', icon: CheckSquareOffset },
  // Chat lives in the top-bar (see ChatHeaderButton) rather than the sidebar —
  // it's one click away from every page with an always-visible unread badge.
  // Team categories was promoted to PRIMARY_NAV (a core setup surface).
  // Signups merged into the Teams page (/teams) — the team list doubles as the
  // public-signup admin overview (queue + per-team config). No separate entry.
  // Shop / lagkassan — team stores, products, campaigns, and the immutable
  // team-fund ledger. Single-page hub with internal tabs; sub-routes for
  // per-team ledger and per-store editor surface via Recently visited.
  { id: 'shop', to: '/shop', i18nKey: 'nav.shop', icon: Storefront },
  // Billing (the invoice list / editor) — the invoice surface. Lives in the
  // "All pages" accordion; the settings/billing sub-page (Stripe Connect
  // onboarding + reminder cadence) remains under Settings as a DEEP_NAV entry.
  { id: 'billing', to: '/billing', i18nKey: 'nav.billing', icon: Receipt },
  // Notices ("aviseringar") lives in PRIMARY_NAV (promoted to a permanent
  // top slot); no duplicate entry here.
  // Pulse — anonymous engagement surveys (members / guardians / leaders).
  // Admins create + launch runs and read the threshold-gated results.
  { id: 'pulse', to: '/pulse', i18nKey: 'nav.pulse', icon: PulseNav },
  // Website is the public org website module — the 4-step wizard + the
  // edit dashboard. Single-page hub (no sub-pages), navigates normally.
  { id: 'website', to: '/website', i18nKey: 'nav.website', icon: Globe },
  // Broadcasts ("utskick") — block-structured emails to org members.
  // Sits next to Website because the two are tightly coupled (broadcasts
  // can publish as news on the website).
  { id: 'broadcasts', to: '/broadcasts', i18nKey: 'nav.broadcasts', icon: EnvelopeSimple },
  // Mobile app — info + per-user app preferences. Promoted out of
  // Settings → App so members find it without opening the settings panel.
  { id: 'app', to: '/settings/app', i18nKey: 'nav.app', icon: DeviceMobile },
  // Proactive assistant — nightly sweep + approval spine for "things that
  // need doing". Org-admin-only in v1; leader routing is Phase 3.
  { id: 'assistant', to: '/assistant', i18nKey: 'nav.assistant', icon: Lightbulb },
  { id: 'settings', opensSettings: true, i18nKey: 'nav.settings', icon: GearSix },
];

export const DEEP_NAV: readonly NavItem[] = [
  {
    id: 'settings-profile',
    to: '/settings/profile',
    i18nKey: 'nav.settingsProfile',
    icon: UserCircle,
  },
  {
    id: 'settings-organization',
    to: '/settings/organization',
    i18nKey: 'nav.settingsOrganization',
    icon: Buildings,
  },
  {
    id: 'settings-activity',
    to: '/settings/activity',
    i18nKey: 'nav.settingsActivity',
    icon: ClockCounterClockwise,
  },
  {
    id: 'settings-administrators',
    to: '/settings/administrators',
    i18nKey: 'nav.settingsAdministrators',
    icon: UserGear,
  },
  {
    id: 'settings-billing',
    to: '/settings/billing',
    i18nKey: 'nav.settingsBilling',
    icon: Receipt,
  },
  {
    id: 'settings-membercard-templates',
    to: '/settings/membercard-templates',
    i18nKey: 'nav.settingsMemberCardTemplates',
    icon: IdentificationCard,
  },
  {
    id: 'settings-feedback',
    to: '/settings/feedback',
    i18nKey: 'nav.settingsFeedback',
    icon: ChatCircleText,
  },
  {
    id: 'settings-danger-zone',
    to: '/settings/danger-zone',
    i18nKey: 'nav.settingsDangerZone',
    icon: ShieldWarning,
  },
];

// Items eligible for the "Recently visited" list — DEEP_NAV sub-pages
// only. PRIMARY_NAV and HUB_NAV both have permanent slots elsewhere
// (top section and "Alla sidor" respectively); surfacing them in
// Senast besökta would just duplicate what's already on screen.
// Recents exist to give sub-pages a way to bubble up — that's the
// only thing this list does.
export const TRACKABLE_NAV: readonly NavItem[] = [...DEEP_NAV];
